import { useCallback, useMemo } from 'react'
import dayjs from 'dayjs'
import { useLocalStorage } from 'usehooks-ts'

import usePromiseDialog from '@shared/hooks/src/usePromiseDialog'
import { useMe } from '@shared/providers/src/MeProvider'

import ConfirmationV2 from '@components/Dialog/ConfirmationV2'

/**
 * Universal hook for showing a confirmation dialog before executing an action
 * when certain conditions are met. Supports "once per day per entity" tracking.
 *
 * @param {Object} options
 * @param {Function} options.shouldShowConfirmation - Function that returns boolean if confirmation should be shown
 * @param {Function} options.getConfirmationProps - Function that returns props for the confirmation dialog
 * @param {React.Component} options.component - Dialog component to use (defaults to ConfirmationV2)
 * @param {string} options.entityKey - Unique key for tracking (e.g., 'gilead-{patientId}-{section}')
 * @returns {Function} wrappedAction - Function that wraps the original action with conditional confirmation
 */
export default function useConditionalAction({
  shouldShowConfirmation,
  getConfirmationProps,
  component = ConfirmationV2,
  entityKey = undefined,
}) {
  const me = useMe()
  const [confirmationWarnings, setConfirmationWarnings] = useLocalStorage('conditional-actions', {})

  const showConfirmationDialog = usePromiseDialog({
    component,
    getProps: getConfirmationProps,
  })

  const hasShownToday = useMemo(() => {
    if (!entityKey) return false

    const warningKey = `[${me.id}]:${entityKey}`
    const lastShownDate = confirmationWarnings[warningKey]
    const today = dayjs().format('L')

    return lastShownDate === today
  }, [entityKey, me.id, confirmationWarnings])

  const markAsShownToday = useCallback(() => {
    if (!entityKey) return

    const warningKey = `[${me.id}]:${entityKey}`
    const today = dayjs().format('L')

    setConfirmationWarnings((prev) => ({ ...prev, [warningKey]: today }))
  }, [entityKey, me.id, setConfirmationWarnings])

  return useCallback(
    async (originalAction) => {
      if (!shouldShowConfirmation() || hasShownToday) return originalAction()

      const confirmed = await showConfirmationDialog()

      if (confirmed) {
        markAsShownToday()
        return originalAction()
      }

      // User canceled, don't execute action
      return Promise.resolve()
    },
    [shouldShowConfirmation, hasShownToday, showConfirmationDialog, markAsShownToday]
  )
}
